import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import ConfirmationDialog from './ConfirmationDialog';
import PlayerFormDialog from './PlayerFormDialog';
import PlayerCard from './PlayerCard';
import AppLayout from '../../../components/ui/AppLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SectionCard from '../../../components/ui/SectionCard';
import StatCard from '../../../components/ui/StatCard';
import EmptyState from '../../../components/ui/EmptyState';
import api from '../../../api';

// Modal para Criar Torneio
const TournamentFormDialog = ({ open, onClose, onCreate }) => {
  const [formData, setFormData] = useState({ nome: '', data: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = () => {
    onCreate(formData);
    setFormData({ nome: '', data: '' }); // Resetar form após criação
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Novo torneio</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Nome do torneio" name="nome" value={formData.nome} onChange={handleChange} fullWidth />
          <TextField label="Data" type="date" name="data" value={formData.data} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">Criar</Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminPlayersPage = () => {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [showTournamentDialog, setShowTournamentDialog] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  useEffect(() => {
    async function fetchTournaments() {
      try {
        const response = await api.get('/getTorneios');
        setTournaments(response.data);
        const ativo = response.data.find((t) => t.status === 'Ativo') || response.data[response.data.length - 1];
        if (ativo) setSelectedTournament(ativo._id);
      } catch (error) {
        console.error('Erro ao buscar torneios:', error);
      }
    }
    fetchTournaments();
  }, []);

  async function fetchPlayers() {
    try {
      const response = await api.get(`players/torneio/${selectedTournament}`);
      setPlayers(response.data.players);
    } catch (error) {
      setPlayers([]);
    }
  }

  useEffect(() => {
    if (selectedTournament) {
      fetchPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTournament]);

  const handleCreateTournament = async (tournament) => {
    try {
      const response = await api.post('/torneio/create', tournament);
      setTournaments([...tournaments, response.data]);
      setShowTournamentDialog(false);
      setSnackbar({ severity: 'success', message: 'Torneio criado.' });
    } catch (error) {
      setSnackbar({ severity: 'error', message: error.response?.data?.error || 'Erro ao criar torneio.' });
    }
  };

  const handleDeletePlayer = (player) => {
    setConfirmationMessage(`Você realmente deseja excluir o jogador ${player.nome}?`);
    setConfirmationAction(() => () => deletePlayer(player._id));
    setShowConfirmationDialog(true);
  };

  const deletePlayer = async (id) => {
    try {
      await api.remove(`players/${id}`);
      setShowConfirmationDialog(false);
      fetchPlayers();
    } catch (error) {
      setSnackbar({ severity: 'error', message: 'Erro ao excluir jogador.' });
    }
  };

  const handleSavePlayer = async (player) => {
    try {
      if (player._id) {
        await api.put(`players/${player._id}`, player);
      } else {
        await api.post('players', { ...player, torneio_id: selectedTournament });
      }
      setShowFormDialog(false);
      setCurrentPlayer(null);
      fetchPlayers();
      setSnackbar({ severity: 'success', message: 'Jogador salvo.' });
    } catch (error) {
      setSnackbar({ severity: 'error', message: 'Erro ao salvar jogador.' });
    }
  };

  const qtdPago = players.filter(p => p.pago === 'SIM').length;
  const qtdConvidado = players.filter(p => p.nomeConvidado).length;
  const qtdFesta = players.filter(p => p.festa === 'SIM').length;

  return (
    <AppLayout>
      <PageHeader
        eyebrow="Torneio"
        title="Jogadores inscritos"
        subtitle="Pagamentos, camisas e presença na festa."
        actions={
          <>
            <TextField select size="small" label="Torneio" value={selectedTournament} onChange={(e) => setSelectedTournament(e.target.value)} sx={{ minWidth: 220 }}>
              {tournaments.map(tournament => (
                <MenuItem key={tournament._id} value={tournament._id}>{tournament.nome}</MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setShowTournamentDialog(true)}>Novo torneio</Button>
            <Button variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => { setCurrentPlayer(null); setShowFormDialog(true); }}>Adicionar jogador</Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard label="Inscritos" value={players.length} icon={<GroupsRoundedIcon />} tone="brand" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Pagos" value={qtdPago} hint={`${players.length - qtdPago} pendentes`} icon={<PaidRoundedIcon />} tone="success" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Convidados" value={qtdConvidado} icon={<PersonAddAlt1RoundedIcon />} tone="info" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Na festa" value={qtdFesta} icon={<CelebrationRoundedIcon />} tone="gold" /></Grid>
      </Grid>

      <SectionCard title="Elenco">
        {players.length === 0 ? (
          <EmptyState title="Nenhum jogador inscrito neste torneio" />
        ) : (
          <Grid container spacing={2}>
            {players.map(player => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={player._id}>
                <PlayerCard player={player} onEdit={(p) => { setCurrentPlayer(p); setShowFormDialog(true); }} onDelete={handleDeletePlayer} />
              </Grid>
            ))}
          </Grid>
        )}
      </SectionCard>

      <TournamentFormDialog open={showTournamentDialog} onClose={() => setShowTournamentDialog(false)} onCreate={handleCreateTournament} />
      <PlayerFormDialog open={showFormDialog} onClose={() => { setShowFormDialog(false); setCurrentPlayer(null); }} player={currentPlayer} onSave={handleSavePlayer} />
      <ConfirmationDialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)} onConfirm={confirmationAction} message={confirmationMessage} />
      <Snackbar open={Boolean(snackbar)} autoHideDuration={5000} onClose={() => setSnackbar(null)}>
        {snackbar ? <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>{snackbar.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default AdminPlayersPage;
